import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings, FONT_OPTIONS } from "@/lib/settings";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UploadField from "./UploadField";

const COLOR_FIELDS = [
  ["bg", "Fond principal"],
  ["section", "Fond des sections"],
  ["border", "Touches claires"],
  ["accent", "Rose accent"],
  ["dark", "Texte & zones foncées"],
  ["muted", "Texte secondaire"],
];

const Label = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">{children}</label>
);

export default function SitePanel() {
  const { settings, refresh } = useSettings();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(settings), [settings]);

  const set = (section, key, value) => setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", {
        colors: draft.colors,
        fonts: draft.fonts,
        texts: draft.texts,
        images: draft.images,
        salon: draft.salon,
      });
      await refresh();
      toast.success("Personnalisation enregistrée — visible immédiatement sur le site !");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-2 h-12 rounded-none border-black/10 bg-transparent font-light";

  return (
    <div className="mt-10 grid gap-6" data-testid="site-panel">
      <section className="border border-black/5 bg-[var(--brand-bg)] p-8">
        <h2 className="font-display text-2xl text-[var(--brand-dark)]">Couleurs</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {COLOR_FIELDS.map(([key, label]) => (
            <div key={key} className="flex items-center gap-4" data-testid={`color-field-${key}`}>
              <input type="color" value={draft.colors[key]} onChange={(e) => set("colors", key, e.target.value)}
                     className="h-12 w-14 cursor-pointer border border-black/10 bg-transparent p-1" data-testid={`color-input-${key}`} />
              <div>
                <p className="text-sm font-light text-[var(--brand-dark)]">{label}</p>
                <p className="text-xs uppercase text-[var(--brand-muted)]">{draft.colors[key]}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-black/5 bg-[var(--brand-bg)] p-8">
        <h2 className="font-display text-2xl text-[var(--brand-dark)]">Polices</h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <div>
            <Label>Titres</Label>
            <Select value={draft.fonts.heading} onValueChange={(v) => set("fonts", "heading", v)}>
              <SelectTrigger className="mt-2 h-12 rounded-none border-black/10 bg-transparent font-light" data-testid="font-heading-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.heading.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`font-heading-${f.replace(/ /g, "-")}`}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="font-display mt-3 text-2xl text-[var(--brand-dark)]" style={{ fontFamily: `"${draft.fonts.heading}", serif` }}>Aperçu des titres</p>
          </div>
          <div>
            <Label>Textes</Label>
            <Select value={draft.fonts.body} onValueChange={(v) => set("fonts", "body", v)}>
              <SelectTrigger className="mt-2 h-12 rounded-none border-black/10 bg-transparent font-light" data-testid="font-body-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.body.map((f) => (
                  <SelectItem key={f} value={f} data-testid={`font-body-${f.replace(/ /g, "-")}`}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-3 text-sm font-light text-[var(--brand-muted)]" style={{ fontFamily: `"${draft.fonts.body}", sans-serif` }}>Aperçu du texte courant du site.</p>
          </div>
        </div>
      </section>

      <section className="border border-black/5 bg-[var(--brand-bg)] p-8">
        <h2 className="font-display text-2xl text-[var(--brand-dark)]">Textes du héros</h2>
        <div className="mt-6 grid gap-5">
          <div>
            <Label>Sur-titre</Label>
            <Input value={draft.texts.hero_overline} onChange={(e) => set("texts", "hero_overline", e.target.value)} className={inputCls} data-testid="text-hero-overline" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <Label>Ligne {n}{n === 2 ? " (italique)" : ""}</Label>
                <Input value={draft.texts[`hero_line${n}`]} onChange={(e) => set("texts", `hero_line${n}`, e.target.value)} className={inputCls} data-testid={`text-hero-line${n}`} />
              </div>
            ))}
          </div>
          <div>
            <Label>Sous-titre</Label>
            <Textarea value={draft.texts.hero_subtitle} onChange={(e) => set("texts", "hero_subtitle", e.target.value)} className="mt-2 rounded-none border-black/10 bg-transparent font-light" data-testid="text-hero-subtitle" />
          </div>
          <div>
            <Label>Bandeau défilant (un élément par ligne)</Label>
            <Textarea value={(draft.texts.marquee_items || []).join("\n")}
                      onChange={(e) => set("texts", "marquee_items", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
                      className="mt-2 rounded-none border-black/10 bg-transparent font-light" rows={4} data-testid="text-marquee" />
          </div>
        </div>
      </section>

      <section className="border border-black/5 bg-[var(--brand-bg)] p-8">
        <h2 className="font-display text-2xl text-[var(--brand-dark)]">Image principale</h2>
        <div className="mt-6">
          <UploadField label="Photo de la bannière d'accueil" value={draft.images.hero}
                       onChange={(v) => set("images", "hero", v)} testid="hero-image" />
        </div>
      </section>

      <section className="border border-black/5 bg-[var(--brand-bg)] p-8">
        <h2 className="font-display text-2xl text-[var(--brand-dark)]">Coordonnées</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {[["address", "Adresse"], ["phone", "Téléphone"], ["hours", "Horaires"], ["instagram_url", "Lien Instagram"], ["tiktok_url", "Lien TikTok"], ["whatsapp_url", "Lien WhatsApp (wa.me/…)"]].map(([key, label]) => (
            <div key={key}>
              <Label>{label}</Label>
              <Input value={draft.salon[key] || ""} onChange={(e) => set("salon", key, e.target.value)} className={inputCls} data-testid={`salon-${key}`} />
            </div>
          ))}
        </div>
      </section>

      <button onClick={save} disabled={saving}
              className="sticky bottom-6 rounded-full bg-[var(--brand-dark)] px-10 py-4 text-xs uppercase tracking-[0.25em] text-[var(--brand-bg)] shadow-lg transition-transform duration-300 hover:scale-[0.97] disabled:opacity-50"
              data-testid="site-save-btn">
        {saving ? "Enregistrement…" : "Enregistrer la personnalisation"}
      </button>
    </div>
  );
}
